<template>
  <div>
    <PageTitle>
      <span v-html="pageTitle" />
    </PageTitle>
    <PageContent v-if="task">
      <form @submit.prevent="onSubmit">
        <Container>
          <NoticeBox type="error" v-if="hasErrors">
            {{ invalidMessage }}
          </NoticeBox>
          <div class="form-group required">
            <label id="name">タスク名</label>
            <input
              id="name"
              type="text"
              class="form-control"
              :class="{ 'has-error': hasError('task.task_name') }"
              v-model="task.task_name"
            />
            <FormError :errors="getErrors('task.task_name')" />
          </div>
          <div class="form-group required">
            <label for="step">STEP</label>
            <div class="select is-fullwidth">
              <select
                id="step"
                class="form-control"
                :class="{ 'has-error': hasError('task.step_id') }"
                v-model="task.step_id"
              >
                <option :value="0">選択</option>
                <option
                  v-for="step in steps"
                  :key="step.step_id"
                  :value="step.step_id"
                  v-html="step.step_abbreviation"
                ></option>
              </select>
            </div>
            <FormError :errors="getErrors('task.step_id')" />
          </div>
          <div class="form-group required">
            <label for="task-type">タスク種別</label>
            <div class="select is-fullwidth">
              <select
                id="task-type"
                class="form-control"
                :class="{ 'has-error': hasError('task.type_id') }"
                v-model="task.type_id"
              >
                <option :value="0">選択</option>
                <option
                  v-for="type in types"
                  :key="type.task_type_id"
                  :value="type.task_type_id"
                  v-html="type.task_type_name"
                ></option>
              </select>
            </div>
            <FormError :errors="getErrors('task.type_id')" />
          </div>
          <div class="form-group required">
            <label for="link">リンク</label>
            <div class="radio-list">
              <label
                v-for="item in linkTypes"
                :key="`link${item.id}`"
                :for="`link${item.id}`"
                class="radio"
                :class="item.class"
              >
                <input
                  :id="`link${item.id}`"
                  type="radio"
                  name="link-select"
                  :value="item.id"
                  v-model="task.link_type"
                  @change="onLinkTypeChange"
                />
                <span class="radio-text">{{ item.name }}</span>
              </label>
            </div>
            <input
              v-if="isLinkTypeUrl"
              id="link"
              type="text"
              class="form-control"
              :class="{ 'has-error': hasError('task.link_url') }"
              v-model="task.link_url"
              :disabled="isLinkTypeNone"
            />
            <div class="select is-fullwidth" v-else>
              <select
                id="link"
                class="form-control"
                :class="{ 'has-error': hasError('task.link_id') }"
                :disabled="isLinkTypeNone"
                v-model="task.link_id"
              >
                <option value="">選択</option>
                <option
                  v-for="link in linkOptions"
                  :key="link.id"
                  :value="link.id"
                >{{ $domUtils.decode(link.name) }}</option>
              </select>
            </div>
            <FormError :errors="getErrors('task.link_type')" />
            <FormError :errors="getErrors('task.link_id')" />
            <FormError :errors="getErrors('task.link_url')" />
          </div>
          <div class="form-group" v-if="isPlanner">
            <label for="lock" class="checkbox">
              <input
                id="lock"
                type="checkbox"
                v-model="task.lock_flag"
              />
              <span class="checkbox-text">ロック</span>
            </label>
          </div>
          <div class="form-footer">
            <div class="text-center">
              <WaveButton type="submit" :loading="loading">
                保存する
              </WaveButton>
            </div>
            <div class="text-center space-t-3">
              <a href="#" @click.prevent="$router.back()">
                キャンセル
              </a>
            </div>
          </div>
        </Container>
      </form>
    </PageContent>

    <Modal
      id="modal-complete"
      title="保存が完了しました"
      icon="check"
      @ok="modalOk"
    />
  </div>
</template>

<script>
import NoticeBox from '@/components/NoticeBox.vue'
import FormError from '@/components/FormError.vue'
import Modal from '@/components/Modal.vue'
import stepApi from '@/api/step'
import categoryApi from '@/api/category'
import questionApi from '@/api/question'
import menuApi from '@/api/menu'
import { renderError } from '@/utils/error'
import { objExcept, objGet } from '@/utils/object'
import validationMixin from '@/mixins/validation'

const ERROR_NO_CATEGORY = 481

const linkTypes = [
  { id: 1, name: 'なし', class: 'link-none' },
  { id: 2, name: 'カテゴリー', class: 'link-category' },
  { id: 3, name: '一問一答', class: 'link-question' },
  { id: 4, name: '固定ページ', class: 'link-static' },
  { id: 5, name: 'URL', class: 'link-url' },
]

const initialTask = {
  step_id: 0,
  type_id: 0,
  task_id: 0,
  task_name: '',
  link_type: 1,
  link_id: "",
  link_url: "",
  lock_flag: false,
  complete: false,
  unavailable: false,
  revision: 0,
}

export default {
  components: {
    NoticeBox,
    FormError,
    Modal,
  },

  metaInfo() {
    return {
      title: this.pageTitle,
      bodyAttrs: { class: 'step-edit' }
    }
  },

  props: {
    type: {
      type: String,
      required: true,
    }
  },

  mixins: [
    validationMixin,
  ],

  rules(vm) {
    return {
      'task.task_name': ['タスク名', ['required']],
      'task.step_id': ['STEP', [['required', { deny: [0] }]]],
      'task.type_id': ['タスク種別', [['required', { deny: [0] }]]],
      'task.link_type': ['リンク種別', ['required']],
      'task.link_id': ['リンク', [
        [{ handler: vm.linkIdValidator }, null, '$name$を入力してください。']
      ]],
      'task.link_url': ['リンク', [
        [{ handler: vm.linkUrlValidator }, null, '$name$を入力してください。']
      ]],
    }
  },

  data() {
    return {
      loading: false,
      task: null,
      steps: [],
      types: [],
      links: new Map(),
      linkTypes,
    }
  },

  computed: {
    isCreatePage() {
      return this.type === 'create'
    },
    isEditPage() {
      return this.type === 'edit'
    },
    isEditStatusPage() {
      return this.type === 'edit.status'
    },
    pageTitle() {
      return this.$store.getters['app/getMenuName']('step')
    },
    cancelPath() {
      return this.$wcRouter.path({ name: 'step.index' })
    },
    businessId() {
      return this.$store.getters['auth/businessId']
    },
    stepId() {
      return this.$route.params.stepId
    },
    taskId() {
      return this.$route.params.taskId
    },
    taskStatusId() {
      return this.isEditStatusPage ? this.taskId : 0
    },
    linkOptions() {
      return this.links.get(this.task.link_type) || []
    },
    isLinkTypeNone() {
      return this.task.link_type === 1
    },
    isLinkTypeUrl() {
      return this.task.link_type === 5
    },
    taskData() {
      return Object.assign({}, objExcept(this.task, 'step_task_id'), {
        task_id: this.task.step_task_id || 0,
        task_status_id: 0,
        business_id: 0,
        office_id: 0,
        link_id: this.task.link_id || 0,
        link_url: this.task.link_url || '',
        complete: objGet(this.task, 'complete', false),
      })
    },
    isPlanner() {
      return this.$store.getters['auth/isPlanner']
    },
  },

  methods: {
    linkIdValidator(val, param, msg) {
      if (this.task.link_type === 1 || this.task.link_type === 5) {
        return [true]
      }

      if (val === '' || val === null) {
        return [false, msg]
      }

      return [true]
    },
    linkUrlValidator(val, param, msg) {
      if (this.task.link_type !== 5) {
        return [true]
      }

      if (val === '' || val === null) {
        return [false, msg]
      }

      return [true]
    },
    async fetchSteps() {
      try {
        this.steps = await stepApi.getStep(this.businessId, {
          task: `false`
        })
      } catch (err) {
        renderError(err)
      }
    },
    async fetchTaskTypes() {
      try {
        this.types = await stepApi.getTaskTypes(this.businessId)
      } catch (err) {
        renderError(err)
      }
    },
    async fetchTask() {
      const fetch = () => {
        if (this.isCreatePage) {
          return new Promise(r => r(Object.assign({}, initialTask)))
        }
        if (this.isEditPage) {
          return stepApi.task(this.businessId, this.taskId)
        }
        if (this.isEditStatusPage) {
          return stepApi.taskStatus(this.businessId, this.taskId)
        }
      }

      try {
        this.task = await fetch()

        if (!this.isCreatePage) {
          this.task.task_name = this.$domUtils.decode(this.task.task_name)
          this.checkOptionExistence()
        }
      } catch (err) {
        renderError(err)
      }
    },
    checkOptionExistence() {
      // STEP
      if (!this.steps.map(s => s.step_id).includes(this.task.step_id)) {
        this.task.step_id = 0
      }

      // STEP 種別
      if (!this.types.map(s => s.task_type_id).includes(this.task.type_id)) {
        this.task.type_id = 0
      }

      // リンク
      if (!this.linkOptions.map(o => o.id).includes(this.task.link_id)) {
        this.task.link_id = ""
      }
    },
    async fetchCategories() {
      const response = await categoryApi.get(this.businessId)
      return response.map(c => ({
        id: c.category_id,
        name: c.category_name,
      }))
    },
    async fetchQuestions() {
      const response = await questionApi.dialogs(this.businessId)
      return response.map(q => ({
        id: q.dialog_id,
        name: q.dialog_title,
      }))
    },
    async fetchStaticPages() {
      const response = await menuApi.get(this.businessId)
      return response.menu
        .filter(m => m.menu_url.href.function_code === 'static')
        .map(m => ({
          id: m.menu_url.href.static_page_id * 1,
          name: m.menu_name
        }))
    },
    async fetchLinks() {
      try {
        const [questions, staticPages] = await Promise.all([
          this.fetchQuestions(),
          this.fetchStaticPages(),
        ])

        this.links.set(1, [])
        this.links.set(3, questions)
        this.links.set(4, staticPages)
      } catch (err) {
        renderError(err)
      }
      try {
        this.links.set(2, await this.fetchCategories())
      } catch (err) {
        // カテゴリが0件の場合APIエラーになるが、エラー表示にしない
        if (err.response && err.response.status === ERROR_NO_CATEGORY) {
          this.links.set(2, [])
          return
        }
        renderError(err)
      }
    },
    async onSubmit() {
      if (this.loading) {
        return false
      }

      if (!this.validate()) {
        window.scrollTo(0, 0)
        return false
      }

      this.loading = true

      try {
        await stepApi.updateStatus(
          this.businessId,
          this.taskStatusId,
          this.taskData
        )

        // Modal Open
        this.$modal.show('modal-complete')
      } catch (err) {
        renderError(err)
        this.loading = false
      }
    },
    onLinkTypeChange() {
      this.task.link_id = ''
    },
    modalOk() {
      this.$wcRouter.push({
        name: 'step.index',
        params: { id: this.task.step_id }
      })
    },
  },

  created() {
    this.$withLoading(async () => {
      await Promise.all([
        this.fetchSteps(),
        this.fetchTaskTypes(),
        this.fetchLinks(),
      ])

      this.fetchTask()
    })
  },
}
</script>
